import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";

const fmtISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function BookingCalendar({ config }) {
  const { lang, t } = useLanguage();
  const b = t.contacts.booking;
  const locale = lang === "it" ? "it-IT" : "en-GB";
  const openDays = config.days || [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [slotList, setSlotList] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const isDayOpen = (d) => openDays.includes(d.getDay());
  const canGoPrev = month > new Date(today.getFullYear(), today.getMonth(), 1);

  const firstWeekday = (month.getDay() + 6) % 7;
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1)),
  ];
  const weekdayNames = Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: "short" })
  );

  const selectDate = (d) => {
    if (!d || d < today || !isDayOpen(d)) return;
    setSelectedDate(d);
    setSelectedTime(null);
    setSlotList([]);
    setLoadingSlots(true);
    api
      .get("/booking/slots", { params: { date: fmtISO(d) } })
      .then((res) => setSlotList(res.data))
      .catch(() => setSlotList([]))
      .finally(() => setLoadingSlots(false));
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async () => {
    if (!selectedDate || !selectedTime) return;
    setSubmitting(true);
    try {
      await api.post("/booking", {
        name: form.name,
        phone: form.phone,
        email: form.email.trim() || undefined,
        notes: form.notes || undefined,
        date: fmtISO(selectedDate),
        time: selectedTime,
      });
      toast.success(b.success);
      setSlotList(slotList.map((s) => (s.time === selectedTime ? { ...s, taken: true } : s)));
      setSelectedTime(null);
      setForm({ name: "", phone: "", email: "", notes: "" });
    } catch (err) {
      if (err.response?.status === 409) toast.error(b.booked);
      else toast.error(formatApiErrorDetail(err.response?.data?.detail) || b.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-slate-200 bg-white" data-testid="booking-calendar">
      <div className="p-7 sm:p-10 border-b border-slate-200">
        <p className="eyebrow flex items-center gap-2">
          <CalendarDays className="h-4 w-4" /> {b.eyebrow}
        </p>
        <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-light text-slate-900 tracking-tight">{b.title}</h2>
        <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-slate-600">{b.sub}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 p-7 sm:p-10">
        <div>
          <p className="eyebrow">{b.pickDay}</p>
          <div className="mt-4 flex items-center justify-between">
            <button
              data-testid="booking-month-prev"
              onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
              disabled={!canGoPrev}
              className="p-2 text-slate-500 hover:text-brand disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Mese precedente"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <p className="font-serif text-xl text-slate-900 capitalize">
              {month.toLocaleDateString(locale, { month: "long", year: "numeric" })}
            </p>
            <button
              data-testid="booking-month-next"
              onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
              className="p-2 text-slate-500 hover:text-brand"
              aria-label="Mese successivo"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-widest text-slate-400">
            {weekdayNames.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) =>
              d === null ? (
                <span key={`empty-${i}`} />
              ) : (
                <button
                  key={fmtISO(d)}
                  data-testid={`booking-day-${fmtISO(d)}`}
                  onClick={() => selectDate(d)}
                  disabled={d < today || !isDayOpen(d)}
                  className={`aspect-square rounded-full text-sm transition-colors duration-200 ${
                    selectedDate && fmtISO(selectedDate) === fmtISO(d)
                      ? "bg-brand text-white"
                      : d < today || !isDayOpen(d)
                        ? "text-slate-300 cursor-not-allowed"
                        : "text-slate-700 hover:bg-brand-soft"
                  }`}
                >
                  {d.getDate()}
                </button>
              )
            )}
          </div>
        </div>

        <div>
          <p className="eyebrow">{b.pickTime}</p>
          {!selectedDate && <p className="mt-4 text-sm font-light text-slate-400">{b.pickDayFirst}</p>}
          {selectedDate && !loadingSlots && slotList.length === 0 && (
            <p className="mt-4 text-sm font-light text-slate-400">{b.noSlots}</p>
          )}
          {loadingSlots && <p className="mt-4 text-sm font-light text-slate-400">{t.articles.loading}</p>}
          <div className="mt-4 flex flex-wrap gap-2">
            {slotList.map((slot) => (
              <button
                key={slot.time}
                data-testid={`booking-slot-${slot.time.replace(":", "")}`}
                disabled={slot.taken}
                onClick={() => setSelectedTime(slot.time)}
                className={`rounded-full border px-4 py-2 text-sm transition-colors duration-200 ${
                  selectedTime === slot.time
                    ? "bg-brand text-white border-brand"
                    : slot.taken
                      ? "border-slate-100 text-slate-300 line-through cursor-not-allowed"
                      : "border-slate-200 text-slate-700 hover:border-brand hover:text-brand"
                }`}
              >
                {slot.time}
              </button>
            ))}
          </div>

          {selectedTime && (
            <div className="mt-8" data-testid="booking-details">
              <p className="eyebrow">{b.details}</p>
              <div className="mt-4 space-y-3">
                <input
                  data-testid="booking-input-name"
                  required
                  minLength={2}
                  placeholder={b.name}
                  value={form.name}
                  onChange={set("name")}
                  className="w-full border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-brand"
                />
                <input
                  data-testid="booking-input-phone"
                  required
                  minLength={5}
                  placeholder={b.phone}
                  value={form.phone}
                  onChange={set("phone")}
                  className="w-full border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-brand"
                />
                <input
                  data-testid="booking-input-email"
                  type="email"
                  placeholder={b.email}
                  value={form.email}
                  onChange={set("email")}
                  className="w-full border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-brand"
                />
                <textarea
                  data-testid="booking-input-notes"
                  rows={2}
                  maxLength={1000}
                  placeholder={b.notes}
                  value={form.notes}
                  onChange={set("notes")}
                  className="w-full border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-brand resize-none"
                />
                <button
                  data-testid="booking-confirm-button"
                  onClick={submit}
                  disabled={submitting}
                  className="w-full rounded-full bg-brand px-7 py-3.5 text-sm font-medium text-white hover:bg-brand-dark transition-colors duration-300 disabled:opacity-60"
                >
                  {submitting
                    ? b.submitting
                    : `${b.confirm} · ${selectedDate.toLocaleDateString(locale, { day: "numeric", month: "long" })} ${selectedTime}`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
